// WebRTC for online play, in the host page (the game's worker has no network): one RTCPeerConnection per other
// player with two pre-negotiated data channels, "r" (reliable, ordered) and "u" (unordered, no retransmits).
// Signalling (offer, answer, ICE) goes through the room server; see session.ts.
import type { Signal } from '../../shared/net.ts';
import { isLocalHostname } from '../lib/config.ts';
import type { Link } from './mesh.ts';

/** Public STUN only. No TURN: it costs money, so it is the user's decision (plan.md §19). */
export const DEFAULT_ICE: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

/**
 * ICE servers. Tests on localhost may set localStorage["rtw:test-net"] = '{"ice":[]}' (host candidates only, no
 * traffic to public STUN servers); ignored on any other hostname.
 */
export function iceServers(loc: { hostname: string } = location): RTCIceServer[] {
  if (!isLocalHostname(loc.hostname)) return DEFAULT_ICE;
  try {
    const v = JSON.parse(localStorage.getItem('rtw:test-net') ?? 'null') as { ice?: unknown } | null;
    if (v && Array.isArray(v.ice) && v.ice.length === 0) return [];
  } catch { /* ignore */ }
  return DEFAULT_ICE;
}

export interface RtcPeer {
  link: Link;
  /** Handle a signal from the other side. */
  signal(s: Signal): Promise<void>;
  close(): void;
}

export interface RtcCallbacks {
  /** Send a signal to the other side (through the room server). */
  sendSignal(s: Signal): void;
  message(reliable: boolean, bytes: Uint8Array): void;
  /** Both channels are open. */
  open(): void;
  /** The connection is over (closed, or ICE failed). */
  closed(): void;
}

export function rtcPeer(offerer: boolean, cb: RtcCallbacks): RtcPeer {
  const pc = new RTCPeerConnection({ iceServers: iceServers() });
  const r = pc.createDataChannel('r', { negotiated: true, id: 0, ordered: true });
  const u = pc.createDataChannel('u', { negotiated: true, id: 1, ordered: false, maxRetransmits: 0 });
  let done = false;
  const pending: RTCIceCandidateInit[] = [];
  const finish = () => { if (!done) { done = true; cb.closed(); } };
  for (const [ch, reliable] of [[r, true], [u, false]] as const) {
    ch.binaryType = 'arraybuffer';
    ch.onmessage = (e) => { if (e.data instanceof ArrayBuffer) cb.message(reliable, new Uint8Array(e.data)); };
    ch.onopen = () => { if (r.readyState === 'open' && u.readyState === 'open') cb.open(); };
    ch.onclose = finish;
  }
  pc.onicecandidate = (e) => {
    if (e.candidate?.candidate) cb.sendSignal({ ice: { candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid, sdpMLineIndex: e.candidate.sdpMLineIndex } });
  };
  pc.onconnectionstatechange = () => { if (pc.connectionState === 'failed' || pc.connectionState === 'closed') finish(); };
  if (offerer) {
    pc.createOffer().then((o) => pc.setLocalDescription(o)).then(() => {
      const d = pc.localDescription;
      if (d && (d.type === 'offer')) cb.sendSignal({ sdp: { type: 'offer', sdp: d.sdp } });
    }).catch(finish);
  }
  const link: Link = {
    open: (reliable) => (reliable ? r : u).readyState === 'open',
    send: (reliable, bytes) => {
      const ch = reliable ? r : u;
      if (ch.readyState !== 'open') return false;
      try { ch.send(bytes); return true; } catch { return false; }
    },
    bufferedR: () => r.bufferedAmount,
    close: () => { try { pc.close(); } catch { /* closed */ } finish(); },
  };
  return {
    link,
    async signal(s) {
      try {
        if ('sdp' in s) {
          if (s.sdp.type === 'offer' && offerer) return; // only the newer peer offers
          if (s.sdp.type === 'answer' && (!offerer || pc.signalingState !== 'have-local-offer')) return;
          await pc.setRemoteDescription(s.sdp);
          for (const c of pending.splice(0)) await pc.addIceCandidate(c).catch(() => {});
          if (s.sdp.type === 'offer') {
            await pc.setLocalDescription(await pc.createAnswer());
            const d = pc.localDescription;
            if (d) cb.sendSignal({ sdp: { type: 'answer', sdp: d.sdp } });
          }
        } else if (pc.remoteDescription) {
          await pc.addIceCandidate(s.ice).catch(() => {});
        } else if (pending.length < 64) pending.push(s.ice);
      } catch { finish(); }
    },
    close: link.close,
  };
}
